<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { TrashIcon, EnvelopeIcon } from '@heroicons/vue/24/outline';
import { UserPlusIcon } from '@heroicons/vue/24/solid';
import AlertModal from '@/components/ui/modals/AlertModal.vue';
import Select from '@/components/ui/select/Select.vue';
import adminAPI from '@/services/api';
import { useNotificationStore } from '@/store';
import { adminRoles } from '@/utils/config/constants';
import BaseButton from '@/components/ui/buttons/BaseButton.vue';

type Location = {
  name: string;
  people: Person[];
};

type Person = {
  id: string;
  name: string;
  email: string;
  role: string;
  gdsSignature?: string | null;
  status?: 'invited' | 'active' | null;
};

/////
function transformUsersToGroupedFormat(users) {
  const result: Location[] = [];

  const groupedUsers = users.reduce((acc, user) => {
    const agencyName = user.travelAgencyAgent?.travelAgency.name
      ? `Agence ${user.travelAgencyAgent?.travelAgency.name}`
      : adminRoles[user.adminRole.name].text;
    if (!acc[agencyName]) {
      acc[agencyName] = [];
    }
    
    acc[agencyName].push({
      id: user.id,
      name: user.fullName,
      email: user.email,
      role: user.adminRole.name,
      gdsSignature: user.travelAgencyAgent?.gdsSignature || null,
      status: user.isActive ? 'active' : (user.invited ? 'active' : null)
    });
    return acc;
  }, {} as { [key: string]: Person[] });

  for (const agencyName in groupedUsers) {
    result.push({
      name: agencyName,
      people: groupedUsers[agencyName]
    });
  }

  return result;
}
const apiResponse = ref();
onMounted(async () => apiResponse.value = await adminAPI.listAdmin());
watch(apiResponse, (data) => {
  if (data) {
    locations.value = transformUsersToGroupedFormat(data);
    people.value = locations.value.map(({ people }) => people).flat()
  }
});

const locations = ref<Location[]>([])
const people = ref<Person[]>([]);

const selectedPeople = ref<string[]>([])
const indeterminate = computed(() => selectedPeople.value.length > 0 && selectedPeople.value.length < people.value.length)

/// Create admin form
const modalOpen = ref(false);

type RoleOption = "CUSTOMER_ADMIN"| "CUSTOMER_AGENT" | "CUSTOMER_ACCOUNT_MANAGER";
const rolesOptions: Array<{ value: RoleOption; label: string; }> = [
  { value: "CUSTOMER_AGENT", label: "Agent" },
  { value: "CUSTOMER_ACCOUNT_MANAGER", label: "Chargé de comptes (AM)" },
  { value: "CUSTOMER_ADMIN", label: "Administrateur" },
];
const defaultForm = {
  email: "",
  firstName: "",
  lastName: "",
  role: rolesOptions[0],
  gdsSignature: undefined,
}
const createAdminForm = ref({...defaultForm});
async function createAdmin() {
  const { email, firstName, lastName, role, gdsSignature } = createAdminForm.value;
  const {success} = await adminAPI.createAdmin([{
    email,
    firstName,
    lastName,
    gdsSignature,
    role: role.value,
  }]);
  if (success) {
    modalOpen.value = false;
    createAdminForm.value = defaultForm;
    useNotificationStore().emitNotification({
      type: 'success',
      title: `Utilisateur "${firstName} ${lastName.toUpperCase()}" ajouté avec succès`
    });
  }
}

async function inviteAdmin() {
  const { success } = await adminAPI.inviteAdmin({
    adminsIds: selectedPeople.value
  });
  if (success) {
    modalOpen.value = false;
    createAdminForm.value = defaultForm;
    useNotificationStore().emitNotification({
      type: 'success',
      title: selectedPeople.value.length === 1
        ? 'Utilisateur invité avec succès'
        : 'Les utilisateurs ont été invités avec succès'
    });
    selectedPeople.value = [];
  }
}
</script>
<template>
<div class="w-full pb-16 overflow-y-auto text-left">

  <AlertModal
    :open="modalOpen"
    type="info"
    title="Créer un agent ou un administrateur"
    confirm-message="Créer l'utilisateur"
    @close="() => {
      createAdminForm = defaultForm;
      modalOpen = false;
    }"
    @confirm="createAdmin"
  >
    <div>
      <form class="pb-8">
        <div class="pb-3">
          <label for="first_name" class="block text-sm font-medium leading-6 text-gray-900">Prénom</label>
          <div class="mt-2">
            <input type="text" v-model="createAdminForm.firstName" name="first_name" id="first_name" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" placeholder="Prénom" />
          </div>
        </div>
        <div class="pb-3">
          <label for="family_name" class="block text-sm font-medium leading-6 text-gray-900">Nom de famille</label>
          <div class="mt-2">
            <input type="text" v-model="createAdminForm.lastName" name="family_name" id="family_name" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" placeholder="Nom de famille" />
          </div>
        </div>
        <div class="pb-3">
          <label for="email" class="block text-sm font-medium leading-6 text-gray-900">Email</label>
          <div class="mt-2">
            <input type="email" v-model="createAdminForm.email" name="email" id="email" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" placeholder="Adresse email" />
          </div>
        </div>
        <div class="pb-3">
          <label for="signature" class="block text-sm font-medium leading-6 text-gray-900">Signature Amadeus</label>
          <div class="mt-2">
            <input type="text" v-model="createAdminForm.gdsSignature" name="signature" id="signature" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" placeholder="Signature GDS" />
          </div>
        </div>
        <Select
          v-model="createAdminForm.role"
          label="Role" :options="rolesOptions" :selected-option="0"
          :block="true"
        />
      </form>
    </div>
  </AlertModal>

  <div class="px-4 pt-12 sm:px-6 lg:px-12">
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-semibold leading-6 text-gray-900 ">Utilisateurs</h1>
        <p class="mt-2 text-sm text-gray-700">Une liste de tous les utilisateurs groupés par agence, y compris leur nom, leur signature, leur e-mail et leur rôle.</p>
      </div>
      <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
        <BaseButton text="Ajouter un utilisateur" :icon="UserPlusIcon" size="md" @click="modalOpen = true" />
      </div>
    </div>
    <div class="flow-root mt-8">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-12">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div v-if="selectedPeople.length > 0" class="absolute top-0 z-50 flex items-center h-12 space-x-3 bg-white left-14 sm:left-12">
            <BaseButton size="sm" :icon="EnvelopeIcon" text="Inviter" @click="inviteAdmin" />
            <BaseButton size="sm" :icon="TrashIcon" text="Désactiver l'accès" :disabled="false" @click="() => useNotificationStore().emitNotification({ title: 'Merci de nous contacter directement', type: 'info' })" />
          </div>
          <div class="border rounded-lg shadow-sm">
            <table class="min-w-full">
            <thead class="">
              <tr>
                <th scope="col" class="relative px-7 sm:w-12 sm:px-6">
                    <input type="checkbox" class="absolute w-4 h-4 -mt-2 text-blue-600 border-gray-300 rounded left-4 top-1/2 focus:ring-blue-600"
                    :checked="indeterminate || (selectedPeople.length === people.length && people.length > 0)" :indeterminate="indeterminate"
                    @change="(event: any) => selectedPeople = event.target?.checked ? people.map((p) => p.id) : []" />
                </th>
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">Nom</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Rôle</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">E-mail</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Statut</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Signature GDS</th>
                <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-3">
                  <span class="sr-only">Modifier</span>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <template v-if="locations.length" v-for="location in locations" :key="location.name">
                <tr class="border-t border-gray-200">
                  <th colspan="7" scope="colgroup" class="py-2 pl-4 pr-3 text-sm font-semibold text-left text-gray-900 bg-gray-50 sm:pl-3">
                    {{ location.name }} <span class="text-gray-700">({{ location.people?.length }})</span>
                  </th>
                </tr>
                <tr v-for="(person, personIdx) in location.people" :key="person.email" :class="[personIdx === 0 ? 'border-gray-300' : 'border-gray-200', 'border-t']">
                  <td class="relative px-7 sm:w-12 sm:px-6">
                    <div v-if="selectedPeople.includes(person.id)" class="absolute inset-y-0 left-0 w-0.5 bg-blue-600"></div>
                    <input type="checkbox" class="absolute w-4 h-4 -mt-2 text-blue-600 border-gray-300 rounded left-4 top-1/2 focus:ring-blue-600" :value="person.id" v-model="selectedPeople" />
                  </td>
                  <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-3">{{ person.name }}</td>
                  <td class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">{{ adminRoles[person.role].text }}</td>
                  <td class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">{{ person.email }}</td>
                  <td class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                    <span v-if="person.status === 'active'" class="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 rounded-md bg-green-50 ring-1 ring-inset ring-green-600/20">Actif</span>
                    <span v-else-if="person.status === 'invited'" class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-600/20">Invité</span>
                    <span v-else-if="person.status == null" class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-600/20">En attente</span>
                  </td>
                  <td class="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">{{ person.gdsSignature }}</td>
                  <td class="relative py-4 pl-3 pr-4 text-sm font-medium text-right whitespace-nowrap sm:pr-3">
                    <a href="#" class="text-blue-600 cursor-not-allowed hover:text-blue-900">
                      Modifier<span class="sr-only">, {{ person.name }}</span>
                    </a>
                  </td>
                </tr>
              </template>
              <!-- Loading state -->
              <template v-else>
                <tr class="border-t border-gray-200">
                  <th colspan="7" scope="colgroup" class="py-2 pl-4 pr-3 text-sm font-semibold text-left text-gray-900 bg-gray-50 sm:pl-3">
                    Agence de voyage
                  </th>
                </tr>
                <tr class="border-t border-gray-200 animate-pulse" v-for="j in 13">
                  <!-- This represents the checkbox column -->
                  <td class="px-3 py-4">
                    <div class="w-6 h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <!-- This represents the person name column -->
                  <td class="px-3 py-4">
                    <div class="w-full h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <!-- This represents the person role column -->
                  <td class="px-3 py-4">
                    <div class="w-full h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <!-- This represents the person email column -->
                  <td class="px-3 py-4">
                    <div class="w-full h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <!-- This represents the person status column -->
                  <td class="px-3 py-4">
                    <div class="w-1/2 h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <!-- This represents the person GDS signature column -->
                  <td class="px-3 py-4">
                    <div class="w-full h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                  <!-- This represents the modify link column -->
                  <td class="px-3 py-4 text-right">
                    <div class="w-1/3 h-6 bg-neutral-100 rounded-xl"></div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          </div>

        </div>
      </div>
    </div>
  </div>
</div>
</template>
